import { useCallback, useMemo } from "react";
import { type ITimeGrouping, TimeGroupingEnum } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { IScheduleChangeTimePreferencesProps } from "@tenant/flights";

export const useScheduleChangeTimePreferences = (
  props: IScheduleChangeTimePreferencesProps
) => {
  const { selectedTimes, setSelected } = props;
  const { t } = useI18nContext();

  const timePreferences: ITimeGrouping[] = useMemo(
    () => [
      {
        grouping: TimeGroupingEnum.EARLY_MORNING,
        label: t(
          "core-flights.postbooking.scheduleChange.form.times.earlyMorning"
        ),
        sublabel: t(
          "core-flights.postbooking.scheduleChange.form.times.earlyMorningDetails"
        ),
      },
      {
        grouping: TimeGroupingEnum.MORNING,
        label: t("core-flights.postbooking.scheduleChange.form.times.morning"),
        sublabel: t(
          "core-flights.postbooking.scheduleChange.form.times.morningDetails"
        ),
      },
      {
        grouping: TimeGroupingEnum.AFTERNOON,
        label: t(
          "core-flights.postbooking.scheduleChange.form.times.afternoon"
        ),
        sublabel: t(
          "core-flights.postbooking.scheduleChange.form.times.afternoonDetails"
        ),
      },
      {
        grouping: TimeGroupingEnum.EVENING,
        label: t("core-flights.postbooking.scheduleChange.form.times.evening"),
        sublabel: t(
          "core-flights.postbooking.scheduleChange.form.times.eveningDetails"
        ),
      },
    ],
    [t]
  );

  const addTimePreference = useCallback(
    (newTimePreference: TimeGroupingEnum) => {
      setSelected([...selectedTimes, newTimePreference]);
    },
    [selectedTimes, setSelected]
  );

  const removeTimePreference = useCallback(
    (existingTimePreference: TimeGroupingEnum) => {
      const newSelected = selectedTimes.filter(
        (time) => time !== existingTimePreference
      );

      setSelected(newSelected);
    },
    [selectedTimes, setSelected]
  );

  const selectTimePreference = useCallback(
    (selectedPreference: TimeGroupingEnum) => {
      if (selectedTimes.includes(selectedPreference)) {
        removeTimePreference(selectedPreference);
      } else {
        addTimePreference(selectedPreference);
      }
    },
    [addTimePreference, removeTimePreference, selectedTimes]
  );

  return useMemo(
    () => ({
      context: {
        timePreferences,
      },
      handlers: {
        selectTimePreference,
      },
    }),
    [selectTimePreference, timePreferences]
  );
};

export type IScheduleChangeTimePreferencesData = ReturnType<
  typeof useScheduleChangeTimePreferences
>;
